import classNames from 'classnames';
import type { Op } from 'quill/core';
import React, { useEffect, useRef, useState } from 'react';
import CommentActivityItem, { isCommentActivity } from './CommentActivityItem';
import * as styles from './ControlBarActivity.module.scss';
import ActivityItem, { ActivitySeparator } from '../../activities/ActivityItem';
import { useAppDispatch } from '../../app/redux/useAppSelector';
import Button from '../../components/Button';
import InfiniteScrollWaypoint from '../../components/InfiniteScrollWaypoint';
import LoadingIndicator from '../../components/LoadingIndicator';
import SVGIcon from '../../components/SVGIcon';
import Timestamp from '../../components/Timestamp';
import Tooltip from '../../components/Tooltip';
import { EditorActions } from '../../editor/editorSlice';
import Comment from '../../editor/formats/Comment';
import { gql } from '../../graphql/generated/gql';
import type { DocumentType } from '../../graphql/generated/gql';
import { PostActivityAction } from '../../graphql/generated/graphql';
import archiveIcon from '../../icons/non-prefilled/16x16/archive.svg';
import boltIcon from '../../icons/non-prefilled/16x16/bolt.svg';
import draftsIcon from '../../icons/non-prefilled/16x16/drafts.svg';
import editIcon from '../../icons/non-prefilled/16x16/edit.svg';
import publishIcon from '../../icons/non-prefilled/16x16/publish.svg';
import reorderIcon from '../../icons/non-prefilled/16x16/reorder.svg';
import unarchiveIcon from '../../icons/non-prefilled/16x16/unarchive.svg';
import unpublishIcon from '../../icons/non-prefilled/16x16/unpublish.svg';
import verifiedIcon from '../../icons/non-prefilled/16x16/verifiedFilled.svg';
import { useGetUser } from '../../user/useAllUsers';
import exhaustiveMatchGuard from '../../utils/exhaustiveMatchGuard';
import getFeature from '../../utils/features';
import Logger from '../../utils/Logger';
import { isCommentOrPublicCommentThread } from '../../utils/post/threadUtils';
import getFirstName from '../../utils/text/getFirstName';
import commentThreadHash from '../../utils/url/commentThreadHash';
import historyPath from '../../utils/url/historyPath';
import postPath from '../../utils/url/postPath';
import { openPricingModal } from '../../utils/url/pricingPath';
import {
  getHistoryRestrictionDate,
  isHistoryAccessible,
} from '../history/partitionHistories';
import Stats from '../Stats';
import useCanDisplayPostHistoryView from '../useCanDisplayPostHistoryView';

export const CONTROL_BAR_ACTIVITY_MIN_HEIGHT = 265;

gql(/** @complexity 2 */ `
  fragment ControlBarActivity_commentThread on CommentThread {
    id
    content
    context
    comments {
      id
      content
      insertedAt
    }
  }
`);

const POST_ACTIVITY_FRAGMENT = gql(`
  fragment ControlBarActivity_activities on PostActivity {
    actedAt
    action
    actors {
      id
      ...useGetUser_user
    }
    source {
      id
      ... on Comment {
        content
        thread {
          id
          ...ControlBarActivity_commentThread
        }
      }
      ... on CommentThread {
        id
        ...ControlBarActivity_commentThread
      }
      ...on History {
        id
        stats {
          ...Stats_stats
        }
        insertedAt
        versionFrom
        versionTo
      }
    }
  }
`);

const POST_FRAGMENT = gql(`
  fragment ControlBarActivity_post on Post {
    id
    threads {
      id
      ... on CommentThread {
        id
        context
        comments {
          id
          insertedAt
          content
          author {
            id
            ...useGetUser_user
          }
        }
      }
    }
    ...historyPath_post
  }
`);

interface ControlBarActivityProps {
  post?: DocumentType<typeof POST_FRAGMENT>;
  activities?: DocumentType<typeof POST_ACTIVITY_FRAGMENT>[];
  unresolvedCommentThreadIds: string[];
  fetchMoreActivities: (() => Promise<void>) | null;
  closePopover: () => void;
  fullHeight?: boolean;
  lastViewedAt: number;
  allowUnresolvedThreadsSection: boolean;
}

const ControlBarActivity = ({
  post,
  activities,
  closePopover,
  unresolvedCommentThreadIds,
  fetchMoreActivities,
  lastViewedAt,
  fullHeight,
  allowUnresolvedThreadsSection,
}: ControlBarActivityProps) => {
  const dispatch = useAppDispatch();
  const [canShowLoader, setCanShowLoader] = useState(false);
  useEffect(() => {
    const timeout = setTimeout(() => setCanShowLoader(true), 500);
    return () => {
      clearTimeout(timeout);
    };
  }, []);

  const focusCommentThread = (threadId: string) => {
    dispatch(
      EditorActions.focusMagneticWall({
        id: threadId,
        type: 'comment',
        isActive: true,
        hasExpandIntent: false,
      }),
    );
    closePopover();
  };

  if (!activities || !post) {
    return (
      canShowLoader && (
        <div className={styles.loader}>
          <LoadingIndicator delay={0} />
        </div>
      )
    );
  }

  return (
    <div
      className={classNames(styles.container, {
        [styles.fullHeight]: fullHeight,
      })}
    >
      {allowUnresolvedThreadsSection && (
        <UnresolvedThreadsSection
          post={post}
          unresolvedCommentThreadIds={unresolvedCommentThreadIds}
          focusThread={focusCommentThread}
        />
      )}
      <AllActivitiesSection
        post={post}
        activities={activities}
        unresolvedCommentThreadIds={unresolvedCommentThreadIds}
        focusCommentThread={focusCommentThread}
        fetchMoreActivities={fetchMoreActivities}
        closePopover={closePopover}
        lastViewedAt={lastViewedAt}
      />
    </div>
  );
};

interface AllActivitiesSectionProps {
  post: DocumentType<typeof POST_FRAGMENT>;
  activities: DocumentType<typeof POST_ACTIVITY_FRAGMENT>[];
  unresolvedCommentThreadIds: string[];
  focusCommentThread: (id: string) => void;
  closePopover: () => void;
  lastViewedAt: number;
  fetchMoreActivities: (() => Promise<void>) | null;
}

const AllActivitiesSection = ({
  post,
  activities,
  unresolvedCommentThreadIds,
  focusCommentThread,
  lastViewedAt,
  fetchMoreActivities,
  closePopover,
}: AllActivitiesSectionProps) => {
  const dispatch = useAppDispatch();
  const canDisplayPostHistoryView = useCanDisplayPostHistoryView();
  const postActivityListItems: ActivityView[] = activities.map(
    (item, index) => ({
      key: `activity-${index}`,
      timestamp: new Date(item.actedAt).getTime(),
      view: isCommentActivity(item.action) ? (
        <CommentPostActivity
          activity={item}
          post={post}
          focusCommentThread={focusCommentThread}
          unresolvedCommentThreadIds={unresolvedCommentThreadIds}
          canDisplayPostHistoryView={!!canDisplayPostHistoryView}
        />
      ) : (
        <StandardPostActivity
          action={item.action}
          activity={item}
          post={post}
          canDisplayPostHistoryView={!!canDisplayPostHistoryView}
        />
      ),
    }),
  );

  const firstActivity = postActivityListItems.at(0);
  const lastActivity = postActivityListItems.at(-1);

  const activitiesHeading =
    firstActivity && isHistoryAccessible(firstActivity.timestamp)
      ? {
          key: 'activities-heading',
          timestamp: Number.MAX_VALUE,
          view: <h3 className={styles.title}>Activities</h3>,
        }
      : null;

  const upgradeClick = () => {
    openPricingModal(dispatch, 'versionHistory');
    closePopover();
  };

  const boltIconRef = useRef<HTMLDivElement>(null);
  const historyRestrictionTime =
    getFeature('version_history') < 365
      ? `${getFeature('version_history')} days ago`
      : 'a year ago';
  const inaccessibleActivitiesHeading =
    lastActivity && !isHistoryAccessible(lastActivity.timestamp)
      ? {
          key: 'inaccessible-activities-heading',
          timestamp: getHistoryRestrictionDate().valueOf(),
          view: (
            <Tooltip
              content={`Upgrade for detailed\n histories older than \n${historyRestrictionTime}`}
              anchorRef={boltIconRef}
            >
              <Button className={styles.title} onClick={upgradeClick}>
                Over {historyRestrictionTime}
                <SVGIcon
                  size={12.5}
                  className={styles.boltIcon}
                  ref={boltIconRef}
                >
                  {boltIcon}
                </SVGIcon>
              </Button>
            </Tooltip>
          ),
        }
      : null;

  const firstSectionTimestamp =
    (activitiesHeading ?? inaccessibleActivitiesHeading)?.timestamp ??
    Number.POSITIVE_INFINITY;

  const lastViewListItem = lastViewedAt && {
    key: 'last-view-item',
    // Don't let the "last viewed at" marker to be displayed above the first section header
    timestamp: Math.min(lastViewedAt, firstSectionTimestamp),
    view: (
      <ActivitySeparator>
        Your last view was <Timestamp date={lastViewedAt} />
      </ActivitySeparator>
    ),
  };

  const listItemsWithHeadings = [
    ...(activitiesHeading ? [activitiesHeading] : []),
    ...(inaccessibleActivitiesHeading ? [inaccessibleActivitiesHeading] : []),
    ...(lastViewListItem ? [lastViewListItem] : []),
    ...postActivityListItems,
  ]
    .toSorted((item1, item2) => item2.timestamp - item1.timestamp)
    .map((activity) => (
      <React.Fragment key={activity.key}>{activity.view}</React.Fragment>
    ));

  return (
    <InfiniteScrollWaypoint
      items={listItemsWithHeadings}
      fetchMore={fetchMoreActivities}
    />
  );
};

interface StandardPostActivityProps {
  action: Exclude<
    PostActivityAction,
    | PostActivityAction.CommentCreate
    | PostActivityAction.CommentReply
    | PostActivityAction.CommentResolve
  >;
  activity: Omit<DocumentType<typeof POST_ACTIVITY_FRAGMENT>, 'action'>;
  post: DocumentType<typeof POST_FRAGMENT>;
  canDisplayPostHistoryView: boolean;
}

const StandardPostActivity = ({
  action,
  activity,
  post,
  canDisplayPostHistoryView,
}: StandardPostActivityProps) => {
  const getUser = useGetUser();
  const actors = activity.actors.map(getUser);

  switch (action) {
    case PostActivityAction.Create:
      return (
        <ActivityItem
          actors={actors}
          timestamp={activity.actedAt}
          icon={<SVGIcon className={styles.createIcon}>{draftsIcon}</SVGIcon>}
          description="Created this post"
        />
      );
    case PostActivityAction.Verify:
      return (
        <ActivityItem
          actors={actors}
          timestamp={activity.actedAt}
          icon={<SVGIcon className={styles.verifyIcon}>{verifiedIcon}</SVGIcon>}
          description="Verified this post"
        />
      );
    case PostActivityAction.Publish:
      return (
        <ActivityItem
          actors={actors}
          timestamp={activity.actedAt}
          icon={<SVGIcon className={styles.publishIcon}>{publishIcon}</SVGIcon>}
          description="Published this post"
        />
      );
    case PostActivityAction.Unpublish:
      return (
        <ActivityItem
          actors={actors}
          timestamp={activity.actedAt}
          icon={
            <SVGIcon className={styles.unpublishIcon}>{unpublishIcon}</SVGIcon>
          }
          description="Unpublished this post"
        />
      );
    case PostActivityAction.Archive:
      return (
        <ActivityItem
          actors={actors}
          timestamp={activity.actedAt}
          icon={<SVGIcon className={styles.archiveIcon}>{archiveIcon}</SVGIcon>}
          description="Archived this post"
        />
      );
    case PostActivityAction.Unarchive:
      return (
        <ActivityItem
          actors={actors}
          timestamp={activity.actedAt}
          icon={
            <SVGIcon className={styles.unarchiveIcon}>{unarchiveIcon}</SVGIcon>
          }
          description="Unarchived this post"
        />
      );
    case PostActivityAction.TransferOwner:
      return (
        <ActivityItem
          actors={actors}
          timestamp={activity.actedAt}
          icon={
            <SVGIcon className={styles.transferIcon}>{reorderIcon}</SVGIcon>
          }
          description={
            <>
              Transferred owner
              {activity.source?.__typename === 'User' && (
                <>
                  {' '}
                  to{' '}
                  <strong>{getFirstName(getUser(activity.source).name)}</strong>
                </>
              )}
            </>
          }
        />
      );
    case PostActivityAction.Edit: {
      const history =
        activity.source?.__typename === 'History' ? activity.source : null;

      if (!history || !actors.length) {
        return null;
      }

      return (
        <ActivityItem
          actors={actors}
          icon={<SVGIcon className={styles.editIcon}>{editIcon}</SVGIcon>}
          timestamp={activity.actedAt}
          description={
            <Stats stats={history.stats} editsLabel="Words edited" />
          }
          href={
            isHistoryAccessible(history.insertedAt) && canDisplayPostHistoryView
              ? historyPath(post, history.versionFrom, history.versionTo)
              : undefined
          }
        />
      );
    }
    default: {
      exhaustiveMatchGuard(action);
      const error = `Unknown activity type: ${action as string}`;
      if (process.env.NODE_ENV !== 'production') {
        throw new Error(error);
      }
      Logger.error(error);
      return null;
    }
  }
};

interface CommentPostActivityProps {
  activity: DocumentType<typeof POST_ACTIVITY_FRAGMENT>;
  post: DocumentType<typeof POST_FRAGMENT>;
  unresolvedCommentThreadIds: string[];
  focusCommentThread: (id: string) => void;
  canDisplayPostHistoryView: boolean;
}

const CommentPostActivity = ({
  activity,
  post,
  unresolvedCommentThreadIds,
  focusCommentThread,
  canDisplayPostHistoryView,
}: CommentPostActivityProps) => {
  const getUser = useGetUser();
  const actors = activity.actors.map(getUser);
  const dispatch = useAppDispatch();

  const content =
    activity.source?.__typename === 'CommentThread' ||
    activity.source?.__typename === 'Comment'
      ? activity.source.content
      : null;

  const thread =
    activity.source?.__typename === 'CommentThread'
      ? activity.source
      : activity.source?.__typename === 'Comment'
        ? activity.source.thread
        : null;

  if (!content || !thread || !isCommentActivity(activity.action)) {
    return null;
  }

  const getContext = () => {
    if (
      activity.action === 'COMMENT_REPLY' &&
      activity.source?.__typename === 'Comment'
    ) {
      const id = activity.source?.id;
      const index = thread.comments.findIndex((comment) => comment.id === id);
      return thread.comments[index - 1]?.content;
    }

    return extractCommentThreadContext(thread.context, thread.id);
  };

  return (
    <CommentActivityItem
      timestamp={activity.actedAt}
      actors={actors}
      type={activity.action}
      content={content}
      context={getContext()}
      {...(unresolvedCommentThreadIds.includes(thread.id)
        ? {
            onClick: () => focusCommentThread(thread.id),
            onMouseEnter: () =>
              dispatch(EditorActions.highlightComment(thread.id)),
            onMouseLeave: () =>
              dispatch(EditorActions.removeMagneticWallHighlight()),
          }
        : {
            href:
              isHistoryAccessible(activity.actedAt) &&
              isHistoryAccessible(thread.comments.at(-1)?.insertedAt) &&
              canDisplayPostHistoryView
                ? `${postPath(post)}/comments${commentThreadHash(thread.id)}`
                : undefined,
          })}
    />
  );
};

interface ActivityView {
  timestamp: number;
  key: string;
  view: React.ReactNode;
}

interface UnresolvedActivitiesSectionProps {
  post: DocumentType<typeof POST_FRAGMENT>;
  focusThread: (id: string) => void;
  unresolvedCommentThreadIds: string[];
}

const UnresolvedThreadsSection = ({
  post,
  focusThread,
  unresolvedCommentThreadIds,
}: UnresolvedActivitiesSectionProps) => {
  const dispatch = useAppDispatch();
  const getUser = useGetUser();

  const visibleCommentThreads = post?.threads
    ?.map((thread) => {
      const lastComment =
        isCommentOrPublicCommentThread(thread) &&
        unresolvedCommentThreadIds.includes(thread.id) &&
        thread.comments.at(-1);

      if (!lastComment) {
        return null;
      }

      return {
        id: thread.id,
        timestamp: new Date(lastComment.insertedAt).getTime(),
        view: (
          <CommentActivityItem
            actors={[getUser(lastComment.author)]}
            type={
              thread.comments.length > 1
                ? PostActivityAction.CommentReply
                : PostActivityAction.CommentCreate
            }
            timestamp={lastComment.insertedAt}
            context={
              thread.comments.at(-2)?.content ??
              extractCommentThreadContext(thread.context, thread.id)
            }
            content={lastComment.content}
            onClick={() => focusThread(thread.id)}
            onMouseEnter={() =>
              dispatch(EditorActions.highlightComment(thread.id))
            }
            onMouseLeave={() =>
              dispatch(EditorActions.removeMagneticWallHighlight())
            }
          />
        ),
      };
    })
    .filter((item) => item != null)
    .toSorted((a, b) => b.timestamp - a.timestamp);

  if (!visibleCommentThreads?.length) {
    return null;
  }

  return (
    <>
      <h3 className={styles.title}>Unresolved</h3>
      <div>
        {visibleCommentThreads.map((thread) => (
          <React.Fragment key={thread.id}>{thread.view}</React.Fragment>
        ))}
      </div>
    </>
  );
};

const extractCommentThreadContext = (
  ops: Op[] | undefined,
  threadId: string,
) => {
  return ops?.filter?.((item: Op) => {
    const commentAttr = item?.attributes?.[Comment.attrName] as
      | string[]
      | undefined;
    return commentAttr?.includes?.(threadId);
  });
};

export default ControlBarActivity;
